// Pub Golf API client — calls the existing production backend.
const API = import.meta.env.VITE_API_BASE || 'https://pubgolfgame.com';

async function request(path, opts = {}) {
  // Spread `opts` FIRST, then override `headers` last, otherwise
  // `opts.headers` would clobber the Content-Type we just set above.
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    // Try to parse a JSON error body — many endpoints return
    // { error: '...', code: '...' } so callers can act on the code.
    let body = null;
    try {
      body = await res.clone().json();
    } catch {
      try {
        body = { error: await res.text() };
      } catch {}
    }
    const message = body?.error || `API ${res.status}: ${res.statusText}`;
    const err = new Error(message);
    err.status = res.status;
    err.code = body?.code || null;
    err.body = body;
    throw err;
  }
  const contentType = res.headers.get('content-type') || '';
  return contentType.includes('application/json') ? res.json() : res.text();
}

const json = (method, path, body) =>
  request(path, {
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

export const api = {
  base: API,

  // ===== Games =====
  createGame: (data) => json('POST', '/api/games', data),
  joinGame: (code) => request(`/api/games/${code}`),
  finishGame: (code, data) => json('POST', `/api/games/${code}/finish`, data),
  getLiveGames: () => request('/api/games/live'),
  getGameHistory: (uid) => request(`/api/users/${uid}/games`),
  transferCaddy: (code, caddyId, newName) =>
    json('POST', `/api/games/${code}/transfer-caddy`, {
      currentCaddyId: caddyId,
      newCaddyName: newName,
    }),
  claimPlayer: (code, data) => json('POST', `/api/games/${code}/claim`, data),

  // ===== AI Generation =====
  generateCourse: (data) => json('POST', '/api/generate', data),
  getGenerations: (uid) => request(`/api/users/${uid}/generations`),

  // ===== Purchases / Premium =====
  getUserPurchases: (uid) => request(`/api/users/${uid}/purchases`),
  createCheckout: (data) => json('POST', '/api/payments/create-checkout', data),
  confirmCheckout: (data) => json('POST', '/api/payments/confirm', data),

  // ===== Profiles =====
  getProfile: (uid) => request(`/api/profiles/${uid}`),
  updateProfile: (data) => json('POST', '/api/profiles', data),
  getProfileStats: (uid) => request(`/api/profiles/${uid}/stats`),
  searchProfiles: (q) => request(`/api/profiles/search?q=${encodeURIComponent(q)}`),

  // ===== Friends =====
  getFriends: (uid) => request(`/api/friends/${uid}`),
  getFriendRequests: (uid) => request(`/api/friends/${uid}/requests`),
  sendFriendRequest: (uid, fid) =>
    json('POST', '/api/friends/request', { userId: uid, friendId: fid }),
  acceptFriendRequest: (uid, fid) =>
    json('POST', '/api/friends/accept', { userId: uid, friendId: fid }),
  declineFriendRequest: (uid, fid) =>
    json('POST', '/api/friends/decline', { userId: uid, friendId: fid }),
  removeFriend: (uid, fid) =>
    json('POST', '/api/friends/remove', { userId: uid, friendId: fid }),

  // ===== Stories =====
  getStoriesFeed: () => request('/api/stories/feed'),
  getStories: (uid) => request(`/api/stories/${uid}`),
  postStory: (formData) =>
    fetch(`${API}/api/stories`, { method: 'POST', body: formData }).then((r) => r.json()),
  deleteStory: (id, uid) =>
    request(`/api/stories/${id}?userId=${encodeURIComponent(uid)}`, { method: 'DELETE' }),

  // ===== Courses =====
  getCourses: () => request('/api/courses'),
  shareCourse: (data) => json('POST', '/api/courses', data),

  // ===== Waitlist =====
  joinWaitlist: (email) => json('POST', '/api/waitlist', { email }),

  // ===== Admin =====
  // Every admin call forwards the key in the x-admin-key header. Pass the
  // key as the last argument to each helper; callers should pull the key
  // from localStorage after the password prompt.
  getAdminOverview: (key) =>
    request('/api/admin/overview', { headers: { 'x-admin-key': key } }),
  getAdminUsers: (key) =>
    request('/api/admin/users', { headers: { 'x-admin-key': key } }),
  getAdminGames: (key) =>
    request('/api/admin/games', { headers: { 'x-admin-key': key } }),
  getAdminPurchases: (key) =>
    request('/api/admin/purchases', { headers: { 'x-admin-key': key } }),
  getAdminGenerations: (key) =>
    request('/api/admin/generations', { headers: { 'x-admin-key': key } }),
  getAdminWaitlist: (key) =>
    request('/api/admin/waitlist', { headers: { 'x-admin-key': key } }),
  getAdminSocial: (key) =>
    request('/api/admin/social', { headers: { 'x-admin-key': key } }),
  getAdminHealth: (key) =>
    request('/api/admin/health', { headers: { 'x-admin-key': key } }),
  cleanTestData: (key) =>
    request('/api/admin/cleanup', {
      method: 'POST',
      headers: { 'x-admin-key': key },
    }),
};

export default api;
